'use client';

import { Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CSSProperties, MouseEvent } from 'react';
import type { CampaignConfig, IndexConfig } from '@/modules/config.interface';

export const DonateButton = ({
	config,
	isFunded,
	style,
	campaignId,
	onClick,
	isSubmitting,
	className,
	disabled,
	color,
	fullWidth,
}: {
	config: IndexConfig | CampaignConfig;
	isFunded: boolean;
	style?: CSSProperties;
	campaignId: string;
	onClick?: (e: MouseEvent) => void;
	isSubmitting?: boolean;
	className?: string;
	disabled?: boolean;
	color?: 'primary' | 'secondary';
	fullWidth?: boolean;
}) => {
	const [trans] = useTranslation();
	const theme = useTheme();
	const corporateTexts = config.metadata.textsCollection.texts;

	const sxbutton = {
		// Button root
		'padding': '12px 18px',
		'height': '40px',
		'textTransform': 'none',
		[theme.breakpoints.up('lg')]: {
			padding: '12px 25px',
		},
		// Button
		'width': '14rem',
		'fontSize': '20px',
		'fontWeight': 900,
		'borderRadius': '3px',
		'backgroundColor': disabled ? 'rgba(0, 0, 0, 0.12)' : '',
		'&:hover': {
			backgroundColor: disabled ? 'rgba(0, 0, 0, 0.12)' : '',
		},
		[theme.breakpoints.down('xs')]: {
			width: '100%',
		},
	};

	if (onClick) {
		return (
			<Button
				variant="contained"
				size="large"
				color={color ?? 'primary'}
				sx={sxbutton}
				className={className}
				disabled={isFunded || isSubmitting}
				style={{
					...style,
					backgroundColor: !disabled && !isFunded ? style?.backgroundColor : undefined,
				}}
				fullWidth={fullWidth}
				onClick={onClick}
			>
				<Typography
					style={{
						color: `${isFunded || disabled ? '#51594f' : 'white'}`,
						fontWeight: 900,
						fontSize: '15px',
					}}
				>
					{isFunded ? trans('campaignFunded') : disabled ? trans('viewInfo') : trans('makeDonation')}
				</Typography>
			</Button>
		);
	}

	return (
		// TODO: We are using a normal link instead of the nextjs one to avoid a weird bug on firefox
		// where after changing locales the soft navigation is broken when we go to the donate page
		<a href={!isFunded && !disabled ? `/${campaignId}/donate` : `/${campaignId}`}>
			<Button
				variant="contained"
				size="large"
				color={color ?? 'primary'}
				sx={sxbutton}
				className={className}
				disabled={isFunded || isSubmitting}
				style={{
					...style,
					backgroundColor: !disabled && !isFunded ? style?.backgroundColor : undefined,
				}}
				fullWidth={fullWidth}
			>
				<Typography
					style={{
						color: `${isFunded || disabled ? '#51594f' : 'white'}`,
						fontWeight: 900,
						fontSize: '15px',
					}}
				>
					{isFunded ? trans('campaignFunded') : disabled ? trans('viewInfo') : trans('makeDonation')}
				</Typography>
			</Button>
		</a>
	);
};
